import { Search, Clock, X, Sparkles } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { trendingTokens } from '@/data/blockchainData';
import { detectSearchType } from '@/services/tronApi';

interface SearchHistoryItem {
  query: string;
  type: 'account' | 'transaction' | 'block';
  timestamp: number;
}

const SEARCH_HISTORY_KEY = 'tron_search_history';
const MAX_HISTORY_ITEMS = 10;

const getSearchHistory = (): SearchHistoryItem[] => {
  try {
    const stored = localStorage.getItem(SEARCH_HISTORY_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const saveSearchHistory = (history: SearchHistoryItem[]) => {
  try {
    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(history));
  } catch (error) {
    console.error('Failed to save search history:', error);
  }
};

export const addToSearchHistory = (query: string, type: 'account' | 'transaction' | 'block') => {
  const history = getSearchHistory();
  const filtered = history.filter(item => item.query !== query);
  const newItem: SearchHistoryItem = { query, type, timestamp: Date.now() };
  const updated = [newItem, ...filtered].slice(0, MAX_HISTORY_ITEMS);
  saveSearchHistory(updated);
};

const removeFromHistory = (query: string) => {
  const history = getSearchHistory();
  const filtered = history.filter(item => item.query !== query);
  saveSearchHistory(filtered);
};

const SearchBar = () => {
  const [query, setQuery] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<SearchHistoryItem[]>([]);
  const [isFocused, setIsFocused] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    setHistory(getSearchHistory());
  }, []);

  const handleSearch = () => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return;
    const searchType = detectSearchType(trimmedQuery);
    navigate(`/search?q=${encodeURIComponent(trimmedQuery)}&type=${searchType}`);
    setShowHistory(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleHistoryClick = (item: SearchHistoryItem) => {
    navigate(`/search?q=${encodeURIComponent(item.query)}&type=${item.type}`);
    setShowHistory(false);
  };

  const handleRemoveHistory = (historyQuery: string, e: React.MouseEvent) => {
    e.stopPropagation();
    removeFromHistory(historyQuery);
    setHistory(getSearchHistory());
  };

  const truncateQuery = (q: string) => {
    if (q.length <= 24) return q;
    return q.slice(0, 10) + '...' + q.slice(-10);
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'account': return t('common.address');
      case 'transaction': return t('common.txn');
      case 'block': return t('common.block');
      default: return type;
    }
  };

  return (
    <div className={`relative z-[40] p-3 sm:p-5 rounded-2xl sm:rounded-3xl bg-gradient-to-br from-background/95 via-muted/60 to-background/95 backdrop-blur-xl border transition-all duration-300 ${isFocused ? 'border-primary/50 shadow-[0_0_30px_hsl(var(--primary)/0.15)]' : 'border-border/50'}`}>
      {/* Glass shine effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      
      <div className="relative flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
        <div className="flex-1 relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 bg-primary/15 rounded-xl flex items-center justify-center">
            <Search className="w-4 sm:w-5 h-4 sm:h-5 text-primary" />
          </div>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => { setShowHistory(true); setIsFocused(true); }}
            onBlur={() => { setTimeout(() => setShowHistory(false), 200); setIsFocused(false); }}
            placeholder={t('search.placeholder')}
            className="w-full pl-14 sm:pl-16 pr-3 sm:pr-4 py-3 sm:py-3.5 bg-muted/50 border border-border/50 rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:bg-muted/80 focus:border-primary/30 transition-all duration-200 text-sm sm:text-base"
          />
          
          {/* Search History Dropdown */}
          {showHistory && history.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-gradient-to-br from-background/98 via-muted/80 to-background/98 backdrop-blur-xl border border-border/50 rounded-xl shadow-2xl z-[200] overflow-hidden">
              <div className="p-2.5 sm:p-3 border-b border-border/50 bg-muted/30">
                <span className="text-xs text-muted-foreground flex items-center gap-2 font-medium">
                  <Clock className="w-3.5 h-3.5" />
                  {t('search.recentSearches', 'Recent Searches')}
                </span>
              </div>
              <div className="max-h-[200px] sm:max-h-[250px] overflow-y-auto">
                {history.map((item) => (
                  <div
                    key={item.query}
                    className="flex items-center justify-between px-3 sm:px-4 py-2.5 hover:bg-primary/5 cursor-pointer group transition-colors"
                    onClick={() => handleHistoryClick(item)}
                  >
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <span className="text-[10px] sm:text-xs px-2 py-0.5 sm:py-1 bg-primary/15 text-primary rounded-lg font-medium border border-primary/20">
                        {getTypeLabel(item.type)}
                      </span>
                      <span className="font-mono text-xs sm:text-sm text-foreground truncate">
                        {truncateQuery(item.query)}
                      </span>
                    </div>
                    <button
                      onClick={(e) => handleRemoveHistory(item.query, e)}
                      className="opacity-100 sm:opacity-0 group-hover:opacity-100 p-1.5 hover:bg-muted rounded-lg transition-all"
                    >
                      <X className="w-3 sm:w-3.5 h-3 sm:h-3.5 text-muted-foreground" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <button 
          onClick={handleSearch}
          className="w-full sm:w-auto px-5 sm:px-6 py-3 sm:py-3.5 bg-gradient-to-r from-primary to-primary/90 text-primary-foreground rounded-xl font-semibold hover:from-primary/90 hover:to-primary/80 transition-all duration-200 shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-0.5 text-sm sm:text-base"
        >
          {t('search.button', 'Search')}
        </button>
      </div>

      {/* Trending tokens */}
      <div className="relative flex items-center gap-2 sm:gap-3 mt-3 sm:mt-4 flex-wrap">
        <span className="text-xs text-muted-foreground flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-primary" />
          {t('search.trending', 'Trending')}:
        </span>
        {trendingTokens.slice(0, 4).map((token) => (
          <button
            key={token.symbol}
            className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-lg bg-muted/50 border border-border/50 text-xs font-medium hover:bg-primary/10 hover:border-primary/30 hover:text-primary transition-all duration-200"
          >
            {token.icon && <span>{token.icon}</span>}
            <span>{token.symbol}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default SearchBar;
